<template>
  <div>
    <dialog-base
      :visible.sync="visible"
      title="Chi tiết phiếu thu"
      size="xl"
      :show-confirm="false"
      :use-panel="false"
      :append-to-body="true"
      top="4vh"
      @cancel="handleClose"
    >
      <!-- ══════════════════════════════════════════
           PHÂN KHU 1: THÔNG TIN HỌC SINH & TỔNG QUAN TÀI CHÍNH
           ══════════════════════════════════════════ -->
      <div class="sk-panel mb-3">
        <div class="sk-panel-head ird-panel-head">
          <div class="sk-panel-icon"><i class="el-icon-user" /></div>
          <div class="ird-header-content">
            <span class="ird-student-name">{{ studentName }}</span>
            <el-tag
              v-if="currentStatus"
              :type="statusTagType(currentStatus)"
              size="small"
              effect="light"
              class="font-semibold"
            >
              {{ statusLabel(currentStatus) }}
            </el-tag>
            <span class="ird-divider">|</span>
            <div class="ird-meta-pill">
              <span class="ird-meta-pill__label">Mã học sinh:</span>
              <span class="ird-meta-pill__value">{{ studentCode }}</span>
            </div>
            <div class="ird-meta-pill">
              <span class="ird-meta-pill__label">Mã phiếu:</span>
              <span class="ird-meta-pill__value">{{ invoiceCode }}</span>
            </div>
            <div class="ird-meta-pill">
              <span class="ird-meta-pill__label">Thời gian:</span>
              <span class="ird-meta-pill__value">Tháng {{ selectedInvoice.month }}/{{ selectedInvoice.year }}</span>
            </div>
            <div class="ird-meta-pill">
              <span class="ird-meta-pill__label">Đợt thu:</span>
              <span class="ird-meta-pill__value">Đợt {{ selectedInvoice.turn }}</span>
            </div>
          </div>
        </div>

        <!-- Thẻ thống kê tài chính KPI -->
        <div v-if="invoiceData.idx && !isLoading" class="ird-kpi-grid mt-3">
          <!-- KPI 1: Thiếu / Thừa trước -->
          <div class="ird-kpi-card">
            <div class="ird-kpi-icon ird-kpi-icon--info">
              <i class="el-icon-time" />
            </div>
            <div class="ird-kpi-body">
              <span class="ird-kpi-label">Thiếu / Thừa trước</span>
              <strong class="ird-kpi-value">
                {{ invoiceData.beforeMoney | formatCurrencyNew }}
              </strong>
            </div>
          </div>

          <!-- KPI 2: Tổng phải thu -->
          <div class="ird-kpi-card">
            <div class="ird-kpi-icon ird-kpi-icon--primary">
              <i class="el-icon-money" />
            </div>
            <div class="ird-kpi-body">
              <span class="ird-kpi-label">Tổng phải thu</span>
              <strong class="ird-kpi-value ird-text-primary">
                {{ invoiceData.sumMoney | formatCurrencyNew }}
              </strong>
            </div>
          </div>

          <!-- KPI 3: Đã thanh toán -->
          <div class="ird-kpi-card">
            <div class="ird-kpi-icon ird-kpi-icon--success">
              <i class="el-icon-circle-check" />
            </div>
            <div class="ird-kpi-body">
              <span class="ird-kpi-label">Đã thanh toán</span>
              <strong class="ird-kpi-value ird-text-success">
                {{ invoiceData.collectedMoney | formatCurrencyNew }}
              </strong>
              <span
                v-if="invoiceData.cashMoney || invoiceData.transferMoney"
                class="ird-kpi-subtext"
              >
                TM: {{ (invoiceData.cashMoney || 0) | formatCurrencyNew }} &bull; CK: {{ (invoiceData.transferMoney || 0) | formatCurrencyNew }}
              </span>
            </div>
          </div>

          <!-- KPI 4: Thiếu / Thừa còn lại -->
          <div class="ird-kpi-card">
            <div
              class="ird-kpi-icon"
              :class="Number(invoiceData.afterMoney) < 0 ? 'ird-kpi-icon--danger' : 'ird-kpi-icon--success'"
            >
              <i class="el-icon-wallet" />
            </div>
            <div class="ird-kpi-body">
              <span class="ird-kpi-label">Thiếu / Thừa còn lại</span>
              <strong
                class="ird-kpi-value"
                :class="Number(invoiceData.afterMoney) < 0 ? 'ird-text-danger' : 'ird-text-success'"
              >
                {{ invoiceData.afterMoney | formatCurrencyNew }}
              </strong>
            </div>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════════
           PHÂN KHU 2: DANH SÁCH CÁC KHOẢN THU CHI TIẾT
           ══════════════════════════════════════════ -->
      <div class="sk-panel sk-panel--flat mt-3">
        <div class="sk-panel-head mb-3">
          <div class="sk-panel-icon sk-panel-icon--success">
            <i class="el-icon-s-order" />
          </div>
          <div>
            <div class="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p class="sk-panel-title">Chi tiết các khoản thu</p>
                <p class="sk-panel-subtitle">Bao gồm khoản thu chính và các khoản phụ phân rã trong phiếu thu</p>
              </div>
              <div>
                <el-tag size="small" type="primary" effect="plain" class="font-semibold">
                  Tổng số: {{ (invoiceData.invoiceDetails || []).length }} khoản thu
                </el-tag>
              </div>
            </div>
          </div>
        </div>

        <!-- Hướng dẫn chỉnh sửa -->
        <div v-if="canEdit" class="sk-note mb-3">
          <i class="el-icon-info text-base" />
          <span>Bấm biểu tượng <strong>Sửa</strong> (<i class="el-icon-edit text-blue-600" />) tại khoản thu chính để chỉnh sửa chi tiết và các khoản phụ bên trong.</span>
        </div>

        <!-- Cảnh báo lỗi tải dữ liệu -->
        <div v-if="loadError" class="sk-note sk-note--danger mb-3 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <i class="el-icon-warning-outline text-base" />
            <span>{{ loadError }}</span>
          </div>
          <el-button size="mini" type="danger" plain icon="el-icon-refresh" @click="fetchInvoiceDetail">Tải lại</el-button>
        </div>

        <!-- Bảng danh sách khoản thu (dạng cây) -->
        <div class="ird-table-wrapper">
          <el-table
            v-loading="isLoading"
            :data="detailRows"
            row-key="rowKey"
            :tree-props="{ children: 'children' }"
            default-expand-all
            border
            class="sk-table"
            style="width: 100%"
            max-height="460"
            empty-text="Không có khoản thu trong phiếu"
          >
            <!-- Khoản thu -->
            <el-table-column label="Khoản thu" prop="revenueName" min-width="260">
              <template slot-scope="{ row }">
                <div class="ird-revenue-cell">
                  <span :class="{'font-bold text-slate-800': !row.isChild, 'text-slate-600': row.isChild}">
                    {{ row.revenueName }}
                  </span>
                  <el-tag v-if="row.isChild" size="mini" type="info" effect="plain" class="ml-1.5 ird-child-tag">
                    Khoản phụ
                  </el-tag>
                </div>
              </template>
            </el-table-column>

            <!-- Đơn vị -->
            <el-table-column label="Đơn vị" prop="unit" width="85" align="center">
              <template slot-scope="{ row }">
                <span class="text-slate-600">{{ row.unit || '—' }}</span>
              </template>
            </el-table-column>

            <!-- Đơn giá -->
            <el-table-column label="Đơn giá" min-width="125" align="right">
              <template slot-scope="{ row }">
                <span class="ird-money">{{ row.price | formatCurrencyNew }}</span>
              </template>
            </el-table-column>

            <!-- Số lượng -->
            <el-table-column label="Số lượng" width="85" align="center">
              <template slot-scope="{ row }">
                <span class="font-medium text-slate-700">{{ formatQuantity(row.quantity) }}</span>
              </template>
            </el-table-column>

            <!-- Giảm giá -->
            <el-table-column label="Giảm giá" min-width="120" align="right">
              <template slot-scope="{ row }">
                <span v-if="row.discount && row.discountType === 'PERCENT'" class="text-amber-600 font-semibold">
                  {{ row.discountNumber }}%
                </span>
                <span v-else-if="row.discount" class="text-amber-600 font-semibold">
                  {{ row.discountNumber | formatCurrencyNew }}
                </span>
                <span v-else class="text-slate-400">—</span>
              </template>
            </el-table-column>

            <!-- Thuế -->
            <el-table-column label="Thuế" min-width="120" align="center">
              <template slot-scope="{ row }">
                <span :class="{'text-slate-400': !row.taxPercentage, 'text-slate-700 font-medium': row.taxPercentage}">
                  {{ formatTax(row.taxPercentage) }}
                </span>
              </template>
            </el-table-column>

            <!-- Thành tiền -->
            <el-table-column label="Thành tiền" min-width="135" align="right">
              <template slot-scope="{ row }">
                <strong class="ird-money ird-money--total">
                  {{ row.amount | formatCurrencyNew }}
                </strong>
              </template>
            </el-table-column>

            <!-- Mô tả -->
            <el-table-column label="Mô tả" prop="description" min-width="160" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <span class="text-slate-500 text-xs">{{ row.description || '—' }}</span>
              </template>
            </el-table-column>

            <!-- Tác vụ -->
            <el-table-column v-if="canEdit" label="Tác vụ" width="80" align="center" fixed="right">
              <template slot-scope="{ row }">
                <el-tooltip v-if="!row.isChild" content="Chỉnh sửa khoản thu" placement="top" :enterable="false">
                  <el-button
                    size="mini"
                    type="primary"
                    icon="el-icon-edit"
                    circle
                    plain
                    :disabled="isLoading || !row.idx"
                    @click="handleEditDetail(row)"
                  />
                </el-tooltip>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- Footer dialog -->
      <div slot="footer" class="sk-dialog__footer">
        <el-button class="btn-cancel" plain @click="handleClose">
          <i class="el-icon-circle-close mr-1" /> Đóng
        </el-button>
      </div>
    </dialog-base>

    <!-- Dialog chỉnh sửa chi tiết khoản thu -->
    <edit-invoice-detail-item-dialog
      ref="editInvoiceDetailItemDialog"
      @saved="handleDetailSaved"
    />
  </div>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import EditInvoiceDetailItemDialog from "@/components/tutition_v3/dialog/EditInvoiceDetailItemDialog.vue";
import ManagingStudentInvoiceService from "@/services/tutition_v3/ManagingStudentInvoiceService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import checkPermission from "@/utils/permission";
import { formatQuantity } from "@/components/tutition_v3/utils/quantity";

export default {
  name: "InvoiceRevenueDetailDialog",
  components: { DialogBase, EditInvoiceDetailItemDialog },
  data() {
    return {
      visible: false,
      isLoading: false,
      selectedInvoice: {},
      invoiceData: {},
      loadError: "",
      requestSequence: 0,
    };
  },
  computed: {
    studentName() {
      return this.invoiceData.fullName || this.selectedInvoice.fullName || "—";
    },
    studentCode() {
      return this.invoiceData.studentCode || this.selectedInvoice.studentCode || "—";
    },
    invoiceCode() {
      return this.invoiceData.invoiceCode || this.selectedInvoice.invoiceCode || "—";
    },
    currentStatus() {
      return this.invoiceData.status || this.selectedInvoice.status || "";
    },
    canEdit() {
      // Chỉ sửa khoản thu khi phiếu chưa đóng 100%, theo trạng thái mới tải từ BE.
      return !this.isLoading
        && this.invoiceData.status === "UNPAID"
        && this.checkPermission(["tuition_payment_v4"]);
    },
    detailRows() {
      const mapRows = (items, parentPath = "") => (items || []).map((item, index) => {
        const rowKey = parentPath ? `${parentPath}-${index}` : `${index}`;
        return {
          ...item,
          rowKey,
          isChild: Boolean(parentPath),
          children: mapRows(item.invoiceDetailItems, rowKey),
        };
      });
      return mapRows(this.invoiceData.invoiceDetails);
    },
  },
  methods: {
    checkPermission,
    formatQuantity,
    open(row) {
      if (!row || !row.idx) {
        NotifyMessage.leftCornerNotificationError("Không tìm thấy thông tin phiếu thu.");
        return;
      }
      this.selectedInvoice = { ...row };
      this.visible = true;
      return this.fetchInvoiceDetail();
    },
    async fetchInvoiceDetail() {
      const invoiceIdx = this.selectedInvoice.idx;
      const requestId = ++this.requestSequence;
      this.isLoading = true;
      this.loadError = "";
      this.invoiceData = {};
      try {
        const response = await ManagingStudentInvoiceService.getInvoiceDetail(invoiceIdx);
        if (requestId !== this.requestSequence || !this.visible) return;
        const data = response?.data?.data;
        if (!data || data.idx !== invoiceIdx) {
          throw new Error(response?.data?.message || "Không thể tải chi tiết phiếu thu.");
        }
        this.invoiceData = data;
      } catch (error) {
        if (requestId !== this.requestSequence || !this.visible) return;
        this.loadError = error?.response?.data?.message || error?.message || "Không thể tải chi tiết phiếu thu.";
        NotifyMessage.leftCornerNotificationError(this.loadError);
      } finally {
        if (requestId === this.requestSequence) this.isLoading = false;
      }
    },
    handleEditDetail(detail) {
      if (!this.canEdit || this.isLoading || detail.isChild) return;
      if (!this.invoiceData.idx || !detail.idx) {
        NotifyMessage.leftCornerNotificationError("Không tìm thấy thông tin khoản thu.");
        return;
      }
      // Dùng idx của phiếu lịch sử đã chọn, kể cả phiếu thuộc lớp/nhóm cũ.
      this.$refs.editInvoiceDetailItemDialog.open(this.invoiceData.idx, detail.idx, detail);
    },
    async handleDetailSaved() {
      this.$emit("saved");
      if (this.visible) await this.fetchInvoiceDetail();
    },
    formatTax(value) {
      if (value === null || value === undefined || value === "") return "—";
      const tax = Number(value);
      if (tax === -2) return "Không chịu thuế";
      if (tax === -1) return "Không kê khai tính/nộp thuế";
      return `${tax}%`;
    },
    statusTagType(status) {
      switch (status) {
        case "FULLY_PAID":
        case "FULLY_PAID_WITH_SURPLUS":
          return "success";
        case "PARTIALLY_PAID":
        case "PAID_WITH_DEFICIT":
          return "warning";
        case "UNPAID":
          return "danger";
        case "HIDE":
          return "info";
        case "OVERPAID":
          return "success";
        default:
          return "info";
      }
    },
    statusLabel(status) {
      const map = {
        UNBILLED: "Chưa có phiếu thu",
        UNPAID: "Chưa đóng 100%",
        PARTIALLY_PAID: "Chưa đóng đủ",
        FULLY_PAID: "Đóng đủ 100%",
        OVERPAID: "Đóng thừa",
        FULLY_PAID_WITH_SURPLUS: "Đóng đủ thừa",
        PAID_WITH_DEFICIT: "Đóng đủ thiếu",
        HIDE: "Ẩn phiếu thu",
      };
      return map[status] || status || "—";
    },
    handleClose() {
      this.visible = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";
@import "@/assets/style/sk-table.scss";

/* ─── Header Info & Meta (Sắp xếp 1 dòng) ─────────────── */
.ird-panel-head {
  align-items: center !important;
  margin-bottom: 0 !important;
}

.ird-header-content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  min-width: 0;
  flex: 1;
}

.ird-student-name {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
}

.ird-divider {
  color: #cbd5e1;
  font-weight: 300;
  user-select: none;
}

.ird-meta-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 1.5;
  white-space: nowrap;

  &__label {
    color: #64748b;
    font-weight: 500;
  }

  &__value {
    color: #1e293b;
    font-weight: 600;
  }
}

/* ─── KPI Summary Cards ──────────────────────────────── */
.ird-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.ird-kpi-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
  }
}

.ird-kpi-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;

  &--primary {
    background: #eff6ff;
    color: #2563eb;
  }
  &--info {
    background: #f0f9ff;
    color: #0284c7;
  }
  &--success {
    background: #ecfdf5;
    color: #059669;
  }
  &--danger {
    background: #fef2f2;
    color: #dc2626;
  }
}

.ird-kpi-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ird-kpi-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 2px;
}

.ird-kpi-value {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}

.ird-kpi-subtext {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ird-text-primary {
  color: #2563eb !important;
}

.ird-text-success {
  color: #16a34a !important;
}

.ird-text-danger {
  color: #dc2626 !important;
}

/* ─── Table Structure & Cells ────────────────────────── */
.ird-table-wrapper {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.ird-revenue-cell {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
}

.ird-child-tag {
  font-size: 10px;
  height: 18px;
  line-height: 16px;
  padding: 0 5px;
  border-radius: 4px;
}

.ird-code-badge {
  display: inline-block;
  padding: 2px 7px;
  background: #f1f5f9;
  color: #334155;
  border-radius: 6px;
  font-family: monospace, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
}

.ird-money {
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  font-weight: 500;
  color: #334155;

  &--total {
    font-weight: 700;
    color: #1e293b;
  }
}

/* ─── Table Header Primary (Bắt buộc theo guidelines 10.4) ─── */
/deep/ .sk-table thead th,
/deep/ .sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/deep/ .sk-table thead th > .cell,
/deep/ .sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
  text-align: center;
}

/deep/ .sk-table thead tr {
  height: 48px;
}

/deep/ .sk-table tbody td {
  background: #ffffff;
}

/deep/ .sk-table .el-table__row--level-1 {
  background-color: #f8fafc;
}

/deep/ .sk-table .el-table__row--level-1:hover > td {
  background-color: #f1f5f9 !important;
}

/deep/ .el-button.is-circle {
  border-radius: 50% !important;
}

/* ─── Responsive ─────────────────────────────────────── */
@media (max-width: 992px) {
  .ird-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .ird-kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>

